import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@design-system/shadcn";
import { cn } from "@design-system/utils";
import {
  flexRender,
  type Column,
  type ColumnMeta,
} from "@tanstack/react-table";
import React, { useRef } from "react";

import { useDataTableContext, useGridCellNavigation } from "../hooks";
import type { SortOrder } from "../schemas";

import { CellTruncationTooltip } from "./cell-truncation-tooltip";

const DEFAULT_COLUMN_SIZE = 150;

// Label width approximated, not measured: measuring the header would put content
// back in the width path. 9px overshoots Inter 14px slightly, the safe direction.
const MIN_WIDTH_PER_LABEL_CHARACTER = 9;
/** `TableHead`/`TableCell`'s `px-2`, both sides. */
const CELL_HORIZONTAL_PADDING = 16;
/** The sort control's chevron and the gap before it. */
const SORT_CONTROL_WIDTH = 24;

// Read off the consumer's server-driven search state: TanStack's own sorting is
// disabled here, so `getIsSorted()` is always `false`.
function getAriaSort(
  isSortable: boolean,
  columnId: string,
  sortBy: unknown,
  sortOrder: SortOrder | undefined
): "ascending" | "descending" | "none" | undefined {
  if (!isSortable) return undefined;
  if (sortBy !== columnId) return "none";
  if (sortOrder === "asc") return "ascending";
  if (sortOrder === "desc") return "descending";
  return "none";
}

interface ColumnTotals {
  /** `meta.fixedWidth` columns' pixels, off the table before the rest divide it. */
  pinnedWidth: number;
  weightTotal: number;
  weighted: { size: number; minWidth: number }[];
}

function getColumnMinWidth(column: Column<unknown, unknown>): number {
  const { meta, header } = column.columnDef;
  if (meta?.minWidth != null) return meta.minWidth;

  const label = meta?.label ?? (typeof header === "string" ? header : "");

  return (
    Math.ceil(label.length * MIN_WIDTH_PER_LABEL_CHARACTER) +
    CELL_HORIZONTAL_PADDING +
    (meta?.sortable ? SORT_CONTROL_WIDTH : 0)
  );
}

function getColumnTotals(columns: Column<unknown, unknown>[]): ColumnTotals {
  const totals: ColumnTotals = {
    pinnedWidth: 0,
    weightTotal: 0,
    weighted: [],
  };

  for (const column of columns) {
    if (column.columnDef.meta?.fixedWidth) {
      totals.pinnedWidth += column.getSize();
      continue;
    }
    totals.weightTotal += column.getSize();
    totals.weighted.push({
      size: column.getSize(),
      minWidth: getColumnMinWidth(column),
    });
  }

  return totals;
}

// A weighted column gets `size / weightTotal` of what the pinned pixels leave, so
// its minimum holds from `minWidth x weightTotal / size` of table up.
function getMinTableWidth(totals: ColumnTotals): number {
  const widest = totals.weighted.reduce(
    (floor, { size, minWidth }) =>
      Math.max(floor, (minWidth * totals.weightTotal) / size),
    0
  );

  return totals.pinnedWidth + Math.ceil(widest);
}

// The pinned pixels are NOT subtracted here: under `table-fixed` the browser drops a
// width mixing a percentage with a length (`calc((100% - 100px) * share)`) and splits
// the space evenly instead. Percentages summing to 100% divide what the pins leave.
function getColumnWidth(
  size: number,
  isPinned: boolean,
  totals: ColumnTotals
): string {
  if (isPinned) return `${size}px`;
  return `${(size / totals.weightTotal) * 100}%`;
}

// Under `auto` a width is only a hint, so only a column that asked for one gets it.
function getCellWidth(
  size: number,
  isPinned: boolean,
  layout: "auto" | "fixed",
  totals: ColumnTotals
): React.CSSProperties | undefined {
  if (layout === "fixed")
    return { width: getColumnWidth(size, isPinned, totals) };
  if (size !== DEFAULT_COLUMN_SIZE) return { width: size };
  return undefined;
}

function getCellProps(
  meta: ColumnMeta<unknown, unknown> | undefined,
  size: number,
  layout: "auto" | "fixed",
  totals: ColumnTotals
) {
  return {
    className: cn(
      meta?.align === "right" && "text-right",
      meta?.align === "center" && "text-center"
    ),
    style: getCellWidth(size, Boolean(meta?.fixedWidth), layout, totals),
  };
}

interface TableViewProps {
  /**
   * Opt in to the WAI-ARIA data grid keyboard model: cell-level roving
   * tabindex (single tab stop), 2D arrow navigation, and focus-driven row
   * selection. Off by default so tables that don't need it are unaffected.
   */
  cellNavigation?: boolean;
  /**
   * `fixed` reads every `size` as a WEIGHT and divides the table's width in those
   * ratios, so the rows cannot move the columns; a column with no `size` weighs the
   * table default. `meta.fixedWidth` opts a column out into pixels, `meta.minWidth`
   * sets where it stops shrinking, and an overflowing cell is clipped, never widened.
   * A clipped cell offers its full value in a tooltip on hover; a cell that fits
   * offers none.
   *
   * `auto`, the default, measures the content instead and re-lays the table out for
   * every new page of rows.
   */
  layout?: "auto" | "fixed";
  /** Applied to the table's root element, so consumers need no wrapper div. */
  className?: string;
  "data-testid"?: string;
}

function getRowTabIndex(
  row: { getIsSelected: () => boolean },
  index: number,
  rowSelection: Record<string, unknown>
): number {
  if (row.getIsSelected()) return 0;
  if (index === 0 && Object.keys(rowSelection).length === 0) return 0;
  return -1;
}

function getRowClassName(cellNavigation: boolean, clickable: boolean) {
  return cn(
    clickable &&
      "focus-visible:ring-ring-default focus:outline-none focus-visible:relative focus-visible:z-10 focus-visible:ring-1 focus-visible:ring-inset",
    cellNavigation &&
      "[&_td]:focus-visible:ring-ring-default [&_td]:focus:outline-none [&_td]:focus-visible:ring-1 [&_td]:focus-visible:ring-inset",
    clickable &&
      "hover:bg-shape-neutral-outlined-background-hover cursor-pointer"
  );
}

function TableView({
  cellNavigation = false,
  layout = "auto",
  className,
  "data-testid": dataTestId,
}: Readonly<TableViewProps>) {
  const { table, onRowClick, onRowFocus, onEscape, search, meta } =
    useDataTableContext<unknown, unknown>();

  const containerRef = useRef<HTMLDivElement | null>(null);
  useGridCellNavigation({
    containerRef,
    enabled: cellNavigation,
    onEscape,
  });

  const pageOffset = (search.page - 1) * search.pageSize;
  const headerGroups = table.getHeaderGroups();
  const visibleColumns = table.getVisibleLeafColumns();
  const rows = table.getRowModel().rows;

  // VISIBLE columns, so hiding one hands its share to the rest instead of a gap.
  const totals = getColumnTotals(visibleColumns);

  return (
    // The cell-navigation hook resolves the grid via this container ref.
    <div ref={containerRef} className={className} data-testid={dataTestId}>
      {/* Only `fixed` clips a cell; under `auto` the table widens instead. */}
      {layout === "fixed" && (
        <CellTruncationTooltip containerRef={containerRef} />
      )}
      <Table
        className={cn(
          "my-4",
          // `[&_th]`: a header wider than its column would paint over the next one.
          layout === "fixed" &&
            "table-fixed [&_td]:overflow-hidden [&_td]:text-ellipsis [&_th]:overflow-hidden"
        )}
        style={
          layout === "fixed"
            ? { minWidth: getMinTableWidth(totals) }
            : undefined
        }
        role="grid"
        aria-rowcount={
          meta?.totalCount != null ? meta.totalCount + headerGroups.length : -1
        }
        aria-colcount={visibleColumns.length}
      >
        <TableHeader>
          {headerGroups.map((headerGroup, headerIndex) => (
            <TableRow
              key={headerGroup.id}
              aria-rowindex={headerIndex + 1}
              onFocus={cellNavigation ? () => onRowFocus?.(null) : undefined}
            >
              {headerGroup.headers.map((header) => {
                const cellProps = getCellProps(
                  header.column.columnDef.meta,
                  header.getSize(),
                  layout,
                  totals
                );

                const ariaSort = getAriaSort(
                  Boolean(header.column.columnDef.meta?.sortable),
                  header.column.id,
                  search.sortBy,
                  search.sortOrder
                );

                return (
                  <TableHead
                    key={header.id}
                    aria-sort={ariaSort}
                    {...cellProps}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length ? (
            rows.map((row, index) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                aria-rowindex={pageOffset + index + headerGroups.length + 1}
                aria-selected={row.getIsSelected() || undefined}
                // In cell-navigation mode the roving tabindex lives on the cells
                // (managed by useGridCellNavigation), so the row itself is never
                // a tab stop. For clickable rows, use roving tabindex so keyboard
                // users can reach and activate them; non-clickable rows need no tab stop.
                tabIndex={
                  cellNavigation || !onRowClick
                    ? undefined
                    : getRowTabIndex(row, index, table.getState().rowSelection)
                }
                onClick={() => onRowClick?.(row.original)}
                onKeyDown={
                  onRowClick && !cellNavigation
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(row.original);
                        }
                      }
                    : undefined
                }
                onFocus={
                  cellNavigation ? () => onRowFocus?.(row.original) : undefined
                }
                className={getRowClassName(cellNavigation, !!onRowClick)}
              >
                {row.getVisibleCells().map((cell) => {
                  const cellProps = getCellProps(
                    cell.column.columnDef.meta,
                    cell.column.getSize(),
                    layout,
                    totals
                  );

                  return (
                    <TableCell key={cell.id} role="gridcell" {...cellProps}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center"
              >
                {/* empty by design; host controls empty state text */}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export { TableView as Table };
export type { TableViewProps };
